import { motion } from 'framer-motion'
import { Heart, Award, Users, TrendingUp } from 'lucide-react'
import { useBooking } from '../contexts/BookingContext'
import { getClasses } from '../data'
import ClassCard from '../components/ClassCard'
import TestimonialsSlider from '../components/TestimonialsSlider'
import { DanceClass } from '../core'
import { useState, useEffect } from 'react'

export default function Home() {
  const { setIsBookingOpen, setIsDemo, setSelectedClass } = useBooking()
  const classes = getClasses()
  const [videoLoaded, setVideoLoaded] = useState(false)
  const [videoError, setVideoError] = useState(false)
  
  // Slideshow state
  const [currentSlide, setCurrentSlide] = useState(0)
  const slides = [
    '/images/slideshow/dance1.jpeg',  // Change these names to match your files
    '/images/slideshow/dance2.jpeg',
    '/images/slideshow/dance4.jpeg',
    '/images/slideshow/dance5.jpeg',
    '/images/slideshow/dance6.jpeg',
    '/images/slideshow/dance3.jpeg'
  ]

  // Auto-advance slideshow
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    }, 3000) // Change slide every 3 seconds

    return () => clearInterval(interval)
  }, [slides.length])

  // Video loading timeout fallback
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!videoLoaded && !videoError) {
        setVideoError(true)
      }
    }, 5000) // 5 second timeout

    return () => clearTimeout(timer)
  }, [videoLoaded, videoError])

  const handleBookingClick = (isDemo = false, danceClass?: DanceClass) => {
    setIsDemo(isDemo)
    if (danceClass) {
      setSelectedClass(danceClass)
    }
    setIsBookingOpen(true)
  }

  // Video event handlers
  const handleVideoLoad = () => {
    setVideoLoaded(true)
    setVideoError(false)
  }

  const handleVideoError = () => {
    setVideoError(true)
    setVideoLoaded(false)
  }



  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        {/* Background - Video or Gradient Fallback */}
        {!videoError ? (
          <div className="absolute top-16 left-0 right-0 bottom-0 w-full h-full bg-gradient-to-tr from-primary-50 via-secondary-40 to-pink-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              onLoadedData={handleVideoLoad}
              onError={handleVideoError}
            >
              <source src="/videos/intro.mp4" type="video/mp4" />
              <source src="/videos/intro.webm" type="video/webm" />
            </video>
            {/* Video overlay for better text readability */}
            <div className="absolute inset-0 bg-black/70 dark:bg-black/80" />
            {/* Gradient overlay for better visual appeal */}
            <div className="absolute inset-0 bg-gradient-to-tr from-primary-600/20 via-secondary-600/20 to-pink-600/20 dark:from-primary-800/20 dark:via-secondary-800/25 dark:to-pink-800/25" />
          </div>
        ) : (
          /* Fallback Gradient Background */
          <div className="absolute top-16 left-0 right-0 bottom-0 w-full h-full bg-gradient-to-tr from-primary-50 via-secondary-40 to-pink-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900" />
        )}

        {/* Content */}
        <div className={`relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center ${!videoError ? 'text-white dark:text-white' : 'text-gray-900 dark:text-gray-300'}`}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold mb-6 px-4">
              Dance with <span className="gradient-text">Dr. Sophie</span>
            </h1>
            <p className={`text-lg sm:text-xl md:text-2xl mb-8 max-w-3xl mx-auto px-4 ${!videoError ? 'text-white dark:text-gray-300' : 'text-gray-700 dark:text-gray-300'}`}>
              Master the art of dance from Dr. Sophia Mohan. 
              From classical forms to modern styles, discover your rhythm in our welcoming studio.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 px-4">
              <button
                onClick={() => handleBookingClick(false)}
                className="btn btn-primary text-lg px-8 py-4"
              >
                🎭
                Book a Class
              </button>
              <button
                onClick={() => handleBookingClick(true)}
                className="btn btn-outline text-lg px-8 py-4"
              >
                🎯
                Try Free Demo
              </button>
            </div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 max-w-2xl mx-auto px-4"
            >
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-primary-600">5+</div>
                <div className="text-xs sm:text-sm text-white-600">Years of Experience</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-primary-600">500+</div>
                <div className="text-xs sm:text-sm text-white-600">Happy Students</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-primary-600">6</div>
                <div className="text-xs sm:text-sm text-white-600">Dance Styles</div>
              </div>
              <div className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-primary-600">5★</div>
                <div className="text-xs sm:text-sm text-white-600">Average Rating</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* About Dance with Dr. Sophie (Mission, Values, Features) */}
      <section className="section-padding bg-white dark:bg-gray-800">
        <div className="container mx-auto">
          {/* Mission - two columns */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h3 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white">Our <span className="gradient-text">Mission</span></h3>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-4 max-w-2xl">
                At Dance with Dr. Sophie, we believe inclusivity, diversity and dancing is a universal language that transcends
                barriers and brings people together. Our mission is to create an inclusive, supportive
                environment where students of all ages and backgrounds can explore their passion for
                movement and discover the transformative power of dance.
              </p>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl">
                Whether you're taking your first steps or refining advanced technique, whether you want to express yourself or just want to have fun, 
                we got you covered. We're here to guide you with patience, expertise, and genuine care for your growth as both a dancer and an
                individual.
              </p>
            </div>
            <div className="bg-gradient-to-br from-primary-200 to-secondary-200 dark:from-primary-800 dark:to-secondary-800 rounded-2xl h-96 overflow-hidden shadow-lg relative">
              <div className="absolute inset-0 flex">
                {slides.map((slide, index) => (
                  <div
                    key={index}
                    className={`w-full h-full bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ease-in-out absolute inset-0 ${
                      index === currentSlide ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{ backgroundImage: `url("${slide}")` }}
                  />
                ))}
              </div>
              {/* Dots indicator */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full transition-opacity duration-300 ${
                      index === currentSlide ? 'bg-white opacity-100' : 'bg-white opacity-50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-gradient-to-b from-primary-50 to-pink-50 dark:from-gray-800 dark:to-gray-700">
        <div className="container mx-auto">
          {/* Values - soft gradient background row */}
          {/*<div className="rounded-2xl bg-gradient-to-b from-primary-50 to-pink-50 p-8 mb-16 shadow-sm">*/}
          <div className="p-8 mb-16">
            <div className="text-center mb-16">
              <h3 className="text-4xl font-bold text-gray-900 dark:text-white">Our <span className="gradient-text">Values</span></h3>
              {/*<p className="text-gray-600 mt-2 text-sm">The principles that guide everything we do at our studio</p>*/}
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">The principles that guide everything we do at our community.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: <Heart className="w-6 h-6 text-primary-600"/>,
                  title: 'Inclusivity',
                  desc: 'Everyone is welcome regardless of age, experience, or background'
                },
                {
                  icon: <Award className="w-6 h-6 text-primary-600"/>,
                  title: 'Excellence',
                  desc: 'Committed to providing the highest quality dance education'
                },
                {
                  icon: <Users className="w-6 h-6 text-primary-600"/>,
                  title: 'Community',
                  desc: 'Building lasting friendships through shared love of dance'
                },
                {
                  icon: <TrendingUp className="w-6 h-6 text-primary-600"/>,
                  title: 'Growth',
                  desc: 'Supporting every student\'s personal and artistic development'
                }
              ].map((v, i) => (
                <div key={i} className="card p-6 text-center bg-white dark:bg-gray-800">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-700/20 mb-3">
                    {v.icon}

                  </div>
                  <div className="font-semibold mb-1 text-gray-900 dark:text-white">{v.title}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">{v.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Classes Preview */}
      <section id="classes" className="section-padding bg-white dark:bg-gray-900">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6 text-gray-900 dark:text-white">
              Our <span className="gradient-text">Dance Classes</span>
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              From beginner-friendly sessions to advanced fitness classes, we offer a diverse range of 
              dance styles to help you find your perfect rhythm.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
            {classes.slice(0, 6).map((classItem, index) => (
              <motion.div
                key={classItem.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <ClassCard 
                  danceClass={classItem} 
                  onBook={() => handleBookingClick(false, classItem)}
                  onDemo={() => handleBookingClick(true, classItem)}
                />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <button
              onClick={() => window.location.href = '/classes'}
              className="btn btn-secondary text-lg px-8 py-3"
            >
              View All Classes
            </button>
          </motion.div>
        </div>
      </section>
      
      {/* About Preview */}
      <section className="section-padding bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">
                Meet <span className="gradient-text">Dr. Sophia Mohan</span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                Dr. Sophia Mohan is a Dental surgeon by profession and instructor by passion with over 5+ years of experience 
                in various dance forms. She holds multiple degrees and certifications and aims to make 
                dance accessible to everyone.
              </p>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                Her teaching philosophy focuses on building confidence, fostering creativity, and 
                creating a supportive environment where students can express themselves through movement.
              </p>
              <button
                onClick={() => window.location.href = '/about'}
                className="btn btn-primary"
              >
                Learn More About Dr. Sophie
              </button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-primary-400 to-secondary-400 rounded-2xl h-96 flex items-center justify-center text-6xl text-white shadow-2xl">
              <div 
                  className="w-full h-full bg-cover bg-center bg-no-repeat"
                  style={{backgroundImage: 'url("/images/about/instructor-home.jpeg")'}}
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full font-bold text-sm">
                Expert Instructor
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsSlider />
      
      
      

      {/* CTA Section */}
      <section className="section-padding gradient-bg text-white">
        <div className="container mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6 text-white">Ready to Start Dancing?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto text-white">
              Join hundreds of students who have discovered their passion for dance with Dr. Sophia Mohan. 
              Book your first class today!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => handleBookingClick(false)}
                className="btn bg-white text-primary-600 hover:bg-gray-100 text-lg px-8 py-3"
              >
                Book a Class
              </button>
              <button
                onClick={() => handleBookingClick(true)}
                className="btn border-2 border-white text-white hover:bg-white hover:text-primary-600 text-lg px-8 py-3"
              >
                Try Free Demo
              </button>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
